import { cloneDeep, isEqual } from 'lodash';
import { Calendar } from 'sr-types/lib/search/v1/graphql';
import { ScheduleFormWidgetValue } from '../types';
import {
    clearCalculatedFields,
    handleErrorAndClearCalculatedFields,
    hasRequiredScheduleInputFields,
    makeCalculationInputFromScheduleValue,
    makeSafeError,
    makeScheduleValueFromCalculationResult,
    normalizeCalendarValue,
    normalizeDurationValue,
    normalizeTimeFields
} from './common-schedule-utils';
import { calculateSchedule } from './schedule-calculator';

/**
 * Calculates normalized next value for the ScheduleFormWidget by applying normalization and calculation logic.
 */
export const calculateNextStandAloneScheduleFormWidgetValue = async (
    nextValue: ScheduleFormWidgetValue,
    locale: string,
    calendarsData: Calendar[] | undefined
): Promise<{ calculatedNextValue: ScheduleFormWidgetValue; error?: Error }> => {
    let inputScheduleValue = cloneDeep(nextValue);
    let error: Error | undefined = undefined;

    let normalizedScheduleValue = normalizeTimeFields(inputScheduleValue);
    normalizedScheduleValue = normalizeDurationValue(normalizedScheduleValue);
    normalizedScheduleValue = normalizeCalendarValue(normalizedScheduleValue, calendarsData);

    let calculatedNextValue: ScheduleFormWidgetValue;
    if (hasRequiredScheduleInputFields(normalizedScheduleValue)) {
        try {
            const calculationInput = makeCalculationInputFromScheduleValue(normalizedScheduleValue, locale);

            const calculationResult = await calculateSchedule(calculationInput);

            calculatedNextValue = makeScheduleValueFromCalculationResult(normalizedScheduleValue, calculationResult);
        } catch (e) {
            /**
             * Handle calculation errors by clearing relevant calculated fields
             * to maintain a consistent state.
             */
            calculatedNextValue = handleErrorAndClearCalculatedFields(e, normalizedScheduleValue);

            error = makeSafeError(e, 'Unknown error during schedule calculation');
        }
    } else {
        /**
         * Clear calculated fields when required inputs are missing.
         * This avoids getting into an inconsistent state and displaying stale calculated data
         * when the input data that was used to calculate it is no longer present
         * due to some user action.
         */
        calculatedNextValue = clearCalculatedFields(normalizedScheduleValue);
    }

    return { calculatedNextValue, error };
};

/**
 * Deep comparison for ScheduleFormWidgetValue to prevent unnecessary re-renders.
 * Compares all fields including nested objects (duration, location, calendar).
 * Normalizes undefined/empty string for time fields to prevent false inequality.
 */
export function isScheduleValueEqual(a: ScheduleFormWidgetValue, b: ScheduleFormWidgetValue): boolean {
    if (a === b) {
        return true;
    }

    if (!a || !b) {
        return false;
    }

    // Normalize time fields: treat undefined and empty string as equivalent
    const normalizeTime = (time: string | undefined): string => time || '';

    // Compare primitive fields
    if (
        a.name !== b.name ||
        a.durationSpecified !== b.durationSpecified ||
        a.startDate !== b.startDate ||
        normalizeTime(a.startTime) !== normalizeTime(b.startTime) ||
        a.endDate !== b.endDate ||
        normalizeTime(a.endTime) !== normalizeTime(b.endTime)
    ) {
        return false;
    }

    // Compare duration object
    if (a.duration.unit !== b.duration.unit || a.duration.value !== b.duration.value) {
        return false;
    }

    // Compare location objects by label (Location type doesn't have id field)
    if (a.location?.label !== b.location?.label) {
        return false;
    }

    // Compare calendar objects
    if (a.calendar?.id !== b.calendar?.id || !isEqual(a.calendar?.workingTime, b.calendar?.workingTime)) {
        return false;
    }

    return true;
}
